import "../types/index.js";

import { AttributeError } from "../errors/AttributeError.js";

/**
 * @param {HTMLElement} element
 * @param {AttributeBinding} attribute
 * @returns {string|number|boolean|array|object|null}
 */
export function getAttributeValue(element, attribute) {
  return parseAttributeValue(
    attribute,
    element.getAttribute(attribute.attributeName),
  );
}

/**
 * @param {HTMLElement} element
 * @param {AttributeBinding} attribute
 * @param {string|null} value
 */
export function setAttributeValue(
  element,
  { attributeName, type, optional },
  value,
) {
  if (value === null) {
    if (optional || type === Boolean) {
      // Make sure to return here, otherwise we will call setAttribute below
      // and call setAttribute with null which will be coerced to a string!
      return element.removeAttribute(attributeName);
    } else {
      throw new AttributeError(
        `\`null\` is invalid value for required attribute \`${attributeName}\``,
      );
    }
  } else if (typeof value !== type.name.toLowerCase()) {
    throw new AttributeError(
      `\`${value}\` is invalid value for ${type.name} attribute \`${attributeName}\``,
    );
  }
  switch (type) {
    case String:
    case Number:
      return element.setAttribute(attributeName, value);
    case Boolean:
      return value
        ? element.setAttribute(attributeName, "")
        : element.removeAttribute(attributeName);
    case Object:
    case Array:
      return element.setAttribute(attributeName, JSON.stringify(value));
    default:
      throw new AttributeError(
        `Unknown type \`${type}\` for attribute \`${attributeName}\` on \`${element.tagName.toLowerCase()}\``,
      );
  }
}

/**
 * @param {AttributeBinding} attribute
 * @param {string|null} value
 * @returns {string|number|boolean|array|object|null}
 */
export function parseAttributeValue(attribute, value) {
  if (value === null) {
    if (attribute.optional) {
      return null;
    } else if (attribute.type !== Boolean) {
      throw new AttributeError(
        `\`null\` is invalid value for required attribute \`${attribute.attributeName}\``,
      );
    }
  }
  switch (attribute.type) {
    case String:
      return value;
    case Number:
      return parseNumberAttributeValue(attribute, value);
    case Boolean:
      return value !== null;
    case Object:
      return parseObjectAttributeValue(attribute, value);
    case Array:
      return parseArrayAttributeValue(attribute, value);
  }
}

/**
 * @param {AttributeBinding} attribute
 * @param {string} value
 * @returns {number}
 */
function parseNumberAttributeValue({ attributeName }, value) {
  const number = Number(value);
  if (isNaN(number)) {
    throw new AttributeError(
      `\`NaN\` is invalid value for Number attribute \`${attributeName}\``,
    );
  }
  return number;
}

/**
 * @param {AttributeBinding} attribute
 * @param {string} value
 * @returns {object}
 */
function parseObjectAttributeValue({ attributeName }, value) {
  const object = JSON.parse(value);
  if (typeof object !== "object" || Array.isArray(object)) {
    throw new AttributeError(
      `\`${object}\` is invalid value for Object attribute \`${attributeName}\``,
    );
  }
  return object;
}

/**
 * @param {AttributeBinding} attribute
 * @param {string} value
 * @returns {array}
 */
function parseArrayAttributeValue({ attributeName }, value) {
  const array = JSON.parse(value);
  if (!Array.isArray(array)) {
    throw new AttributeError(
      `\`${array}\` is invalid value for Array attribute \`${attributeName}\``,
    );
  }
  return array;
}
